import { useState, type ReactNode } from "react";
import { ChevronRight, ListFilter, PanelLeft } from "lucide-react";
import type { EmptyStateProps, PageHeaderProps } from "../types";
import { Tooltip } from "@/components/ui/misc";
import { useStored } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { useConsoleShell } from "./context";

/** Square two-letter badge for a space (Linear team icon, without color). */
export function SpaceBadge({ name, className }: { name: string; className?: string }) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters = words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : name.trim().slice(0, 2);
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] bg-foreground/[0.09] font-sans text-[8.5px] font-semibold leading-none tracking-[0.02em] text-foreground/75 uppercase ring-1 ring-inset ring-foreground/[0.06]",
        className,
      )}
    >
      {letters.toUpperCase()}
    </span>
  );
}

/** 28px icon button used throughout the Console chrome. */
export const iconButton =
  "inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors duration-100 hover:bg-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 aria-pressed:bg-selected aria-pressed:text-foreground [&_svg]:size-4";

function SidebarToggle() {
  const shell = useConsoleShell();
  return (
    <Tooltip content={shell.sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}>
      <button type="button" className={iconButton} aria-label={shell.sidebarCollapsed ? "Show sidebar" : "Hide sidebar"} onClick={shell.onToggleSidebar}>
        <PanelLeft />
      </button>
    </Tooltip>
  );
}

/**
 * Linear-style top bar: breadcrumb (Area › Space › page), then view controls and a filter toggle
 * that shows or hides the filter row (`below`). Sticky at the top of the main scroll area.
 */
export function ConsolePageHeader({ title, icon, actions, below }: PageHeaderProps) {
  const shell = useConsoleShell();
  const [filtersOpen, setFiltersOpen] = useStored("console.filterRow", true);
  return (
    <header data-console-header className="sticky top-0 z-20 -mx-4 mb-1 bg-background">
      <div className={cn("desk-drag flex h-11 items-center gap-1 border-b border-border pl-2.5 pr-3", shell.sidebarCollapsed && "desk-titlebar")}>
        <SidebarToggle />
        <nav aria-label="Breadcrumb" className="ml-1 flex min-w-0 flex-1 items-center gap-1">
          {shell.crumbs.map((c) => (
            <span key={c.href} className="flex min-w-0 shrink items-center gap-1">
              <a
                href={c.href}
                className="desk-no-drag truncate rounded px-1 py-0.5 text-[13px] text-muted-foreground outline-none transition-colors hover:bg-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                {c.label}
              </a>
              <ChevronRight className="size-3 shrink-0 text-subtle" aria-hidden />
            </span>
          ))}
          <h1 className="flex min-w-0 items-center gap-2 px-1 font-display text-[16px] font-semibold leading-none tracking-[-0.012em] text-foreground">
            {icon && <span className="inline-flex shrink-0 items-center text-foreground/70 [&_svg]:size-4">{icon}</span>}
            <span className="truncate">{title}</span>
          </h1>
        </nav>
        <div className="desk-no-drag flex shrink-0 items-center gap-1.5">
          {actions}
          {below && (
            <Tooltip content={filtersOpen ? "Hide filters" : "Show filters"}>
              <button
                type="button"
                aria-pressed={filtersOpen}
                aria-label="Filters"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border px-2 text-[12.5px] font-medium text-muted-foreground outline-none transition-colors hover:bg-hover hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 aria-pressed:bg-selected aria-pressed:text-foreground [&_svg]:size-3.5"
              >
                <ListFilter />
                Filter
              </button>
            </Tooltip>
          )}
          {shell.toolbar && (
            <div className="ml-0.5 hidden items-center border-l border-border pl-1.5 @min-[720px]/main:flex [&_button]:size-7">{shell.toolbar}</div>
          )}
        </div>
      </div>
      {below && filtersOpen && <div data-console-filter-row className="no-scrollbar flex min-h-10 items-center overflow-x-auto border-b border-border px-4 py-1">{below}</div>}
    </header>
  );
}

export function ConsoleEmptyState({ message, action }: EmptyStateProps) {
  return (
    <div className="flex min-h-11 flex-wrap items-center gap-3 px-2 py-6">
      <p className="text-[13px] text-muted-foreground">{message}</p>
      {action}
    </div>
  );
}

/** Collapsible group header shared by lists and Today. */
export function GroupHeader({
  icon,
  label,
  count,
  collapsed,
  onToggle,
  trailing,
  onAdd,
  addLabel,
}: {
  icon?: ReactNode;
  label: ReactNode;
  count: number;
  collapsed?: boolean;
  onToggle?: () => void;
  trailing?: ReactNode;
  onAdd?: () => void;
  addLabel?: string;
}) {
  const [hover, setHover] = useState(false);
  const inner = (
    <>
      {onToggle && (
        <ChevronRight
          aria-hidden
          className={cn("-ml-[18px] size-3 shrink-0 text-subtle opacity-0 transition-[transform,opacity] duration-100 group-hover/gh:opacity-100", !collapsed && "rotate-90")}
        />
      )}
      {icon && <span className="inline-flex shrink-0 items-center [&_svg]:size-3.5">{icon}</span>}
      <span className="truncate text-[13px] font-medium text-foreground">{label}</span>
      <span className="text-[12.5px] text-subtle tabular">{count}</span>
    </>
  );
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="group/gh -mx-4 flex h-9 items-center gap-2 border-b border-border/70 bg-foreground/[0.028] pl-6 pr-4 dark:bg-white/[0.022]"
    >
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          className="-my-1 flex min-w-0 items-center gap-2 rounded py-1 pr-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          {inner}
        </button>
      ) : (
        <h2 className="flex min-w-0 items-center gap-2 font-sans tracking-normal">{inner}</h2>
      )}
      <div className="ml-auto flex items-center gap-1">
        {trailing}
        {onAdd && (
          <Tooltip content={addLabel ?? "New task"}>
            <button
              type="button"
              onClick={onAdd}
              aria-label={addLabel ?? "New task"}
              className={cn(iconButton, "size-6 opacity-0 focus-visible:opacity-100 [&_svg]:size-3.5", hover && "opacity-100")}
            >
              <PlusIcon />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden>
      <path d="M8 3.25v9.5M3.25 8h9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}
